import { FormFieldRenderer } from '@components/forms/FormFieldRenderer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { NestedFieldAccordion } from '@components/ui/nested-field-accordion';
import { createEmptyItem, separateFieldTypes, setNestedValue } from '@utils/nestedAccordion';
import { Check, Plus } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import type { GetFieldSuggestionProps } from '@/hooks/useCaseInputStorage';
import type { FieldGroup, FormField } from '@/types/formSchema';
import { computeAutoCalculatedValue } from '@/utils/autoCalculate';
import { isFieldVisible } from '@/utils/fieldVisibility';

interface FieldGroupRendererProps {
  group: FieldGroup;
  formData: Record<string, any>;
  fieldErrors: Record<string, string>;
  onFieldChange: (fieldId: string, value: any) => void;
  onFieldBlur?: (fieldId: string) => void;
  disabled?: boolean;
  /** Consolidated per-field suggestion props from useCaseInputStorage */
  getFieldSuggestionProps?: GetFieldSuggestionProps;
  /** Case ID for File-type fields on an existing case (enables immediate upload on selection). */
  caseRecordId?: string;
}

export const FieldGroupRenderer: React.FC<FieldGroupRendererProps> = ({
  group,
  formData,
  fieldErrors,
  onFieldChange,
  onFieldBlur,
  disabled = false,
  getFieldSuggestionProps,
  caseRecordId,
}) => {
  // Filter out inferred (auto-calculated) fields, then separate scalar from nested
  const visibleFields = useMemo(
    () => group.fields.filter(f => isFieldVisible(f, formData)),
    [group.fields, formData],
  );
  const { scalarFields, nestedFields } = useMemo(
    () => separateFieldTypes(visibleFields),
    [visibleFields],
  );

  // Track which array accordions are expanded
  const [expandedArrays, setExpandedArrays] = useState<string[]>([]);
  // Track expanded items within nested accordions (for auto-expand on add)
  const [nestedExpandedItems, setNestedExpandedItems] = useState<string[]>([]);

  // Determine grid columns based on number of scalar fields
  const getGridClass = (fieldCount: number) => {
    // Single field takes full width
    if (fieldCount === 1) {
      return '';
    }
    // Two or more fields use responsive grid
    return 'md:grid-cols-2';
  };

  /**
   * Handle data changes from nested accordion
   * Supports dot-notation paths like "drug_details.0.code"
   */
  const handleNestedDataChange = (path: string, value: any): void => {
    const keys = path.split('.');
    const fieldId = keys[0];

    if (keys.length === 1) {
      // Direct field update (e.g., replacing entire array)
      onFieldChange(fieldId, value);
    } else {
      // Deep update for nested paths
      const currentData = formData[fieldId] || [];
      const updatedData = setNestedValue(currentData, keys.slice(1).join('.'), value);
      onFieldChange(fieldId, updatedData);
    }
  };

  /**
   * Add a new empty item to an array field
   */
  const handleAddArrayItem = (field: FormField): void => {
    const newItem = createEmptyItem(field);
    const currentItems = Array.isArray(formData[field.id]) ? formData[field.id] : [];
    onFieldChange(field.id, [...currentItems, newItem]);

    // Auto-expand the array accordion
    if (!expandedArrays.includes(field.id)) {
      setExpandedArrays([...expandedArrays, field.id]);
    }

    // Auto-expand the newly added item inside NestedFieldAccordion
    const newItemPath = `${field.id}.${currentItems.length}`;
    setNestedExpandedItems(prev => [...prev, newItemPath]);
  };

  return (
    <div className="field-group">
      {/* Render scalar fields in grid */}
      {scalarFields.length > 0 && (
        <div className={`grid grid-cols-1 gap-6 ${getGridClass(scalarFields.length)}`}>
          {scalarFields.map(field => (
            <FormFieldRenderer
              key={field.id}
              field={{
                ...field,
                disabled:
                  field.disabled ||
                  disabled ||
                  (!!field.mirrorFieldId &&
                    (!field.mirrorWhenField ||
                      (field.mirrorWhenValues?.length
                        ? field.mirrorWhenValues.includes(
                            String(formData[field.mirrorWhenField] ?? ''),
                          )
                        : field.mirrorWhenValue !== undefined
                          ? String(formData[field.mirrorWhenField]) === field.mirrorWhenValue
                          : true))),
              }}
              value={formData[field.id]}
              onChange={value => {
                onFieldChange(field.id, value);
                // Auto-calculate derived fields
                const result = computeAutoCalculatedValue(field, value);
                if (result) {
                  onFieldChange(result.targetFieldId, result.value);
                }
              }}
              onBlur={() => onFieldBlur?.(field.id)}
              error={fieldErrors[field.id]}
              getFieldSuggestionProps={getFieldSuggestionProps}
              caseRecordId={caseRecordId}
            />
          ))}
        </div>
      )}

      {/* Render array/object fields as accordions */}
      {nestedFields.length > 0 && (
        <div className={scalarFields.length > 0 ? 'mt-6 pt-6 border-t' : ''}>
          <Accordion
            type="multiple"
            value={expandedArrays}
            onValueChange={setExpandedArrays}
            className="space-y-2"
          >
            {nestedFields.map(field => {
              const fieldData = formData[field.id];
              const itemCount = Array.isArray(fieldData) ? fieldData.length : 0;
              const hasData =
                field.type === 'array'
                  ? itemCount > 0
                  : fieldData && Object.keys(fieldData).length > 0;
              const itemLabel = field.item_label || field.label || 'Item';

              return (
                <AccordionItem
                  key={field.id}
                  value={field.id}
                  data-field-id={field.id}
                  className="border rounded-md"
                >
                  <AccordionTrigger className="px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none">
                    <div className="flex items-center gap-2 flex-1">
                      <span>
                        {field.label}
                        {field.validation?.required && (
                          <span className="text-destructive ml-1">*</span>
                        )}
                      </span>
                      {itemCount > 0 && (
                        <span className="text-xs text-muted-foreground">
                          ({itemCount} {itemCount === 1 ? 'item' : 'items'})
                        </span>
                      )}
                      {hasData && <Check className="w-4 h-4 text-green-600" />}
                    </div>
                    {!disabled && field.type === 'array' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          handleAddArrayItem(field);
                        }}
                        className="h-6 px-2 text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add {itemLabel}
                      </Button>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <NestedFieldAccordion
                      field={field}
                      data={fieldData}
                      path={field.id}
                      depth={1}
                      isEditable={!disabled}
                      onDataChange={handleNestedDataChange}
                      fieldError={fieldErrors}
                      expandedItems={nestedExpandedItems}
                      onExpandedItemsChange={setNestedExpandedItems}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}
    </div>
  );
};
